import React, { useState } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import { ToastContainer, toast } from 'react-toastify';
import ErrorCard from '../Components/ErrorCard';
import useIAList from '../Hooks/NisHooks/useIAList';
import useISList from '../Hooks/NisHooks/useISList';
import useAddMachineries from '../Hooks/NisMachineriesHooks/useAddMachineries';
import MunicipalitySelector from '../Components/Municipality';

const NISmachineriesAdd = ({ darkMode, onClose, onAddSuccess }) => {
  const { addNISmachineries, error, loading } = useAddMachineries();
  const { iaList, loading: iaLoading, error: iaError } = useIAList();
  const { isList, loading: isLoading, error: isError } = useISList();
  const [validationErrors, setValidationErrors] = useState({});

  const initialFormData = {
    is_name: '',
    years: '',
    ia_name: '',
    barangay: '',
    municipality: '',
    province: '',
    machineriesEquipment: '',
    no_unit: '',
    year_acquired: '',
    functional: '',
    not_functional: '',
    mode_acquisition: '',
    partner_shareholder: '',
    yes_Reg_num: '',
    no_Reg_num: '',
  };

  const [formData, setFormData] = useState(initialFormData);
   const [isExpanded] = useState(true);

  const formSections = [
    { fields: ['years'] },
    { fields: ['ia_name', 'is_name'] },
    { title: ' ADDRESS', fields: ['barangay', 'municipality', 'province'] },
    { fields: ['machineriesEquipment'] },
    { fields: ['no_unit', 'year_acquired'] },
    { title: 'STATUS', fields: ['functional', 'not_functional'] },
    { fields: ['mode_acquisition', 'partner_shareholder'] },
    { title: 'Status of Registration to ABEMIS', fields: ['yes_Reg_num', 'no_Reg_num'] },
  
  ];

  const fieldLabelMap = {
    is_name: 'IMO/ System ',
    years: 'Year',
    ia_name: "Name of Irrigators' Association",
    barangay: 'BARANGAY',
    municipality: 'MUNICIPALITY',
    province: 'PROVINCE',
    machineriesEquipment: 'NAME OF FARM MACHINERIES & EQUIPMENT',
    no_unit: 'NO. OF UNIT',
    year_acquired: 'YEAR ACQUIRED',
    functional: 'FUNCTIONAL',
    not_functional: 'NOT FUNCTIONAL',
    mode_acquisition: 'MODE OF ACQUISITION  (GRANT,RCEF,INCOME,ETC)',
    partner_shareholder: 'PARTNER SHAREHOLDERS',
    yes_Reg_num: 'YES ( Registration No.)',
    no_Reg_num: 'NO',
  };

const validateForm = () => {
  const errors = {};
  const requiredFields = ['years', 'ia_name', 'is_name']; // only these must be filled

  requiredFields.forEach((field) => {
    if (!formData[field] || formData[field].toString().trim() === '') {
      errors[field] = true;
    }
  });

  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
};


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill out all fields.', { autoClose: 3000 });
      return;
    }

    const result = await addNISmachineries(formData);
    if (result?.success) {
      onAddSuccess?.(result.message || 'NIS Machineries data successfully added!');
      setFormData(initialFormData);
      setValidationErrors({});
      handleClose();
    } else {
      toast.error(result.message || 'Failed to add NIS data.', { autoClose: 3000 });
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    onClose();
  };

  return (
    <>
    <ToastContainer />
    <Container darkMode={darkMode} isExpanded={isExpanded}>
      <FormContainer darkMode={darkMode} isExpanded={isExpanded}>
        <Title darkMode={darkMode}>
          <span>NIS Machineries Form</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
          {(loading || iaLoading || isLoading) && (
            <LoaderOverlay><Loader /></LoaderOverlay>

        )}
          {(error || iaError || isError) && (
            <ErrorCard errorMessage={error || iaError || isError} onClose={handleClose} />
          )}

        <form onSubmit={handleSubmit}>
          {formSections.map((section, index) => (
            <CardSection darkMode={darkMode} key={index}>
              {section.title && (
                <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
              )}
              {section.fields.map((key) => (
                <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                  <Label htmlFor={key} hasError={validationErrors[key]}>
                    {fieldLabelMap[key]}
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </Label>
                  {key === 'years' ? (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder="Enter Year (e.g., 2023)"
                      aria-label={fieldLabelMap[key]}
                      maxLength="4"
                      hasError={validationErrors[key]}
                    />
                  ) : key === 'is_name' ? (
                    <>
                      <Select
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        hasError={validationErrors[key]}
                      >
                        <option value="" disabled>Select IS Name</option>
                        {isList.map((is) => (
                          <option key={is.id || is.is_name} value={is.is_name}>
                            {is.is_name}
                          </option>
                        ))}
                        <option value="Others ...">Add new IS</option>
                      </Select>
                      {formData.is_name === 'Others ...' && (
                        <Input
                          type="text"
                          id="custom_is_name"
                          name="custom_is_name"
                          value={formData.custom_is_name}
                          onChange={handleChange}
                          placeholder="Enter Custom IS Name"
                          aria-label={fieldLabelMap.custom_is_name}
                          hasError={validationErrors.custom_is_name}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                    </>
                  ) : key === 'ia_name' ? (
                    <>
                      <Select
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        hasError={validationErrors[key]}
                      >
                        <option value="" disabled>Select IA Name</option>
                        {iaList.map((ia) => (
                          <option key={ia.ia_id || ia.id} value={ia.ia_name}>
                            {ia.ia_name}
                          </option>
                        ))}
                        <option value="Others ...">Add new IA</option>
                      </Select>
                      {formData.ia_name === 'Others ...' && (
                        <Input
                          type="text"
                          id="custom_ia_name"
                          name="custom_ia_name"
                          value={formData.custom_ia_name}
                          onChange={handleChange}
                          placeholder="Enter Custom IA Name"
                          aria-label={fieldLabelMap.custom_ia_name}
                          hasError={validationErrors.custom_ia_name}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                    </>
                  ) : key === 'municipality' ? (
                    <MunicipalitySelector
                      formData={formData}
                      setFormData={setFormData}
                      darkMode={darkMode}
                      hasError={validationErrors[key]}
                    />
                 ) : key === 'dateOrganized' || key === 'secDate' ? (
                    <Input
                      type="date"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      aria-label={fieldLabelMap[key]}
                      hasError={validationErrors[key]}
                    />
                  ) : (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={
                        key === 'contactNum'
                          ? 'Enter Contact Number (7-15 digits)'
                          : key === 'ia_tin'
                          ? 'Enter TIN (alphanumeric, up to 20 chars)'
                          : fieldLabelMap[key]
                      }
                      aria-label={fieldLabelMap[key]}
                      maxLength={key === 'contactNum' ? '15' : key === 'ia_tin' ? '20' : '100'}
                      hasError={validationErrors[key]}
                    />
                  )}
                </FieldGroup>
              ))}
            </CardSection>
          ))}
          <FormButtonContainer>
            <SubmitButton darkMode={darkMode} type="submit">
              Add Information
            </SubmitButton>
          </FormButtonContainer>
        </form>
      </FormContainer>
    </Container>
    </>
  );
};

export default NISmachineriesAdd;

const Container = styled.div`
  padding: 20px;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  position: relative;
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) => (isExpanded ? 'translateX(0%)' : 'translateX(80%)')};
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? 'url(/path/to/adminBg.jpg)' : 'none')};
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  transition: width 0.3s ease, transform 0.3s ease;
  width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 200px)')};

  @media (max-width: 1024px) {
    width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 150px)')};
  }

  @media (max-width: 768px) {
    width: 100%;
    transform: translateX(0);
  }
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    flex: 1;
    text-align: center;
  }
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }
`;

const CloseButton = styled.button`
  background Alone: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;